<template>
  <div v-if="isReady">
    <deal-markup-form
      :can-show-auction-type="false"
      :can-edit-auction-type="false"
      :is-copied-deal="isCopiedDeal"
      @update:pricing-type="(val) => emits('update:pricing-type', val)"
      @update:cpm-fixed="(val) => emits('update:cpm-fixed', val)" />
    <edit-deal-external-deals-form :external-s-s-ps="externalSSPs" />
    <deal-map
      ref="dealMapRef"
      :is-flexible="false"
      :is-preferred-or-n-g-internal-deal="false"
      :is-ready="true"
      :map-suppliers="availableMediaOwners"
      @on-overall-map-selections-changed="(val) => emits('update:map-selections', val)"
      @on-proximity-geofences-changed="(val) => emits('update:proximity-geofences', val)" />
  </div>
</template>

<script setup lang="ts">
  import { computed, inject, onBeforeMount, Ref, ref, useTemplateRef, watch } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import DealMap from '@/common/components/deals/form/DealMap.vue';
  import DealMarkupForm from '@/common/components/deals/form/DealMarkupForm.vue';
  import EditDealExternalDealsForm from '@/common/components/deals/form/EditDealExternalDealsForm.vue';
  import { FilterCollection, Package, QueryParams } from '@/common/models';
  import { useMediaOwners } from '@/org-app/services/tanstack/queries';
  import { useExternalSSPs } from '@/org-app/services/tanstack/queries/externalSSPs.queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  //#region Props

  interface IProps {
    isCopiedDeal: boolean;
  }

  const { isCopiedDeal } = defineProps<IProps>();

  const dealMapRef = useTemplateRef<ComponentExposed<typeof DealMap>>('dealMapRef');
  const deal = inject<Ref<Package> | undefined>('deal');

  //#endregion

  //#region Emits

  const emits = defineEmits([
    'update:pricing-type',
    'update:cpm-fixed',
    'update:map-selections',
    'update:proximity-geofences',
  ]);

  //#endregion

  //#region Data

  const isReady: Ref<boolean> = ref(false);

  const mediaOwnersQuery = useMediaOwners();
  const externalSSPsQuery = useExternalSSPs(undefined, {
    queryParams: QueryParams.all(FilterCollection.for('curate_enabled', [true])),
  });

  //#endregion

  //#region Computed

  const mediaOwners = computed(() => mediaOwnersQuery.items.value);
  const externalSSPs = computed(() => {
    if (mediaOwners.value.length === 0) {
      return [];
    }
    return externalSSPsQuery.items.value.filter((ssp) => mediaOwners.value.some((mo) => mo.sspID === ssp.id));
  });
  const availableMediaOwners = computed(() => {
    if (mediaOwners.value.length === 0) {
      return [];
    }
    if (deal.value?.mediaOwnerIDs.length > 0) {
      return mediaOwners.value.filter((mo) => deal.value?.mediaOwnerIDs.includes(mo.id));
    }
    return mediaOwners.value.filter((mo) => {
      if (deal.value?.mediaOwnerIDs.length > 0) {
        return deal.value?.mediaOwnerIDs.includes(mo.id);
      }
      return externalSSPs.value.some((ssp) => ssp.id === mo.sspID);
    });
  });
  //#endregion

  //#region Lifecycle

  onBeforeMount(async () => {
    await waitForPendingQueries([externalSSPsQuery, mediaOwnersQuery]);
    isReady.value = true;
  });

  watch(
    () => availableMediaOwners.value,
    () => {
      dealMapRef.value?.getFilteredLocations();
    },
  );

  defineExpose({
    dealMapRef,
  });

  //#endregion
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
</style>
